import clsx from "clsx";
import { type MouseEvent, useState } from "react";
import { IconKeyboardDel } from "../ic-key-del";
import { useKeyboard } from "./context";

const buttons = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "del"];

type Ripple = {
  x: number;
  y: number;
  id: number;
};

export function KeyboardDecimal(props: {
  maxDecimal?: number;
  maxLength?: number;
  type?: "number" | "decimal";
  disabled?: boolean;
}) {
  const { maxDecimal, maxLength, type = "decimal", disabled = false } = props;
  const { isVisible, currentValue, updateValue, submit } = useKeyboard();

  const handleClick = (key: string) => {
    if (key === "." && type === "number") {
      return;
    }
    if (key === "del") {
      updateValue(currentValue.slice(0, -1));
      return;
    }
    if (typeof maxLength === "number") {
      const mD = maxDecimal ? maxDecimal + 1 : 0;
      const validate = currentValue.split(".");
      if (
        validate[0] &&
        validate[0].length >= maxLength - mD &&
        key !== "." &&
        !currentValue.includes(".")
      ) {
        return;
      }
    }
    if (typeof maxDecimal === "number") {
      const validate = currentValue.split(".");
      if (validate[1] && validate[1].length >= maxDecimal) {
        return;
      }
    }

    if (key === ".") {
      if (!currentValue.includes(".")) {
        if (currentValue.length === 0) updateValue(currentValue + "0.");
        else updateValue(currentValue + ".");
      }
    } else if (key === "OK") {
      submit();
    } else {
      updateValue(currentValue + key);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="grid grid-cols-3 relative">
      {buttons.map((btn, idx) => (
        <ButtonRipple
          key={`${btn}-${idx}`}
          text={btn}
          handleClick={handleClick}
          index={idx}
          hidden={btn === "." && type === "number"}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function ButtonRipple(props: {
  text: string;
  handleClick: (val: string) => void;
  index: number;
  hidden: boolean;
  disabled?: boolean;
}) {
  const { handleClick, text, index, hidden, disabled = false } = props;
  const [ripples, setRipples] = useState<Ripple[]>([]);

  const createRipple = (e: MouseEvent<HTMLButtonElement>) => {
    const rect = e?.currentTarget?.getBoundingClientRect();
    if (!rect) {
      setRipples([]);
      return;
    }
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();

    setRipples((prev) => [...prev, { x, y, id }]);

    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== id));
    }, 600); // thời gian ripple tồn tại
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        createRipple(e);
        handleClick(text);
      }}
      className={clsx(
        "relative w-14 h-14 rounded-lg text-primary overflow-hidden bg-transparent text-[28px] font-medium flex items-center justify-center hover:bg-content4 peer-focus:bg-content4 active:scale-95 transition duration-150 ease-in-out",
        {
          "justify-self-center": [1, 4, 7, 10].includes(index),
          "justify-self-end": [2, 5, 8, 11].includes(index),
          "opacity-0": hidden,
        }
      )}
    >
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="absolute bg-primary opacity-30 rounded-full animate-ripple"
          style={{
            top: ripple.y - 10,
            left: ripple.x - 10,
            width: 20,
            height: 20,
          }}
        />
      ))}
      {text === "del" ? <IconKeyboardDel /> : text}
    </button>
  );
}
